import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ItemDiscountConfig } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { usePluralization } from 'common/hooks';
import { Typography } from 'common/typography';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { ItemDiscountType } from 'modules/campaigns/types';

import CampaignFormItem from '../../CampaignFormItem';
import { useItemDiscountTypes } from '../useItemDiscountTypes';

type ItemDiscountTypeDetailsProps = {
  itemDiscountConfig: ItemDiscountConfig;
  isEndOfDayCampaign?: boolean;
};

const StyledItemDiscountTypeDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ItemDiscountTypeDetails = ({ itemDiscountConfig, isEndOfDayCampaign }: ItemDiscountTypeDetailsProps) => {
  const { currency } = useCampaignModuleContext();
  const { FormattedPluralMessage } = usePluralization();
  const itemDiscountTypes = useItemDiscountTypes(isEndOfDayCampaign);

  const currentItemDiscount: { type: ItemDiscountType; value: number } | undefined = useMemo(() => {
    if (itemDiscountConfig.amount !== null) {
      return { type: ItemDiscountType.AMOUNT_OFF, value: itemDiscountConfig.amount };
    } else if (itemDiscountConfig.percentage !== null) {
      return { type: ItemDiscountType.PERCENTAGE_OFF, value: itemDiscountConfig.percentage };
    } else if (itemDiscountConfig.free_items_count !== null) {
      return { type: ItemDiscountType.X_FOR_FREE, value: itemDiscountConfig.free_items_count };
    }
    return undefined;
  }, [itemDiscountConfig]);

  const currentItemDiscountPresentational = useMemo(() => {
    if (currentItemDiscount) {
      return itemDiscountTypes.find(({ type }) => type === currentItemDiscount.type);
    }
    return undefined;
  }, [currentItemDiscount, itemDiscountTypes]);

  return (
    <CampaignFormItem
      title={<FormattedMessage id="campaigns.item-discount-type.select-discount" />}
      value={
        <StyledItemDiscountTypeDetailsContainer>
          {currentItemDiscountPresentational && (
            <>
              <Typography.Body3 fontWeight="semiBold">{currentItemDiscountPresentational.title}</Typography.Body3>
              <Typography.Body3>{currentItemDiscountPresentational.description}</Typography.Body3>
              {currentItemDiscount?.type === ItemDiscountType.AMOUNT_OFF && (
                <Typography.Body3 fontWeight="semiBold">
                  <FormattedMessage
                    id="campaigns.item-discount-type.customer-gets"
                    values={{
                      variant: (
                        <>
                          {convertMonetaryIntToDecimal(currentItemDiscount.value, currency)}{' '}
                          <FormattedMessage
                            id="campaigns.item-discount-type.currency-off"
                            values={{
                              currency,
                            }}
                          />
                        </>
                      ),
                    }}
                  />
                </Typography.Body3>
              )}
              {currentItemDiscount?.type === ItemDiscountType.PERCENTAGE_OFF && (
                <Typography.Body3 fontWeight="semiBold">
                  <FormattedMessage
                    id="campaigns.item-discount-type.customer-gets"
                    values={{
                      variant: (
                        <>
                          {currentItemDiscount.value}
                          <FormattedMessage id="campaigns.item-discount-type.percentage-off" />
                        </>
                      ),
                    }}
                  />
                </Typography.Body3>
              )}
              {currentItemDiscount?.type === ItemDiscountType.X_FOR_FREE && (
                <Typography.Body3 fontWeight="semiBold">
                  <FormattedMessage
                    id="campaigns.item-discount-type.customer-gets"
                    values={{
                      variant: (
                        <FormattedPluralMessage
                          id="campaigns.form.item-count.item"
                          count={Number(currentItemDiscount.value)}
                        />
                      ),
                    }}
                  />
                </Typography.Body3>
              )}
            </>
          )}
        </StyledItemDiscountTypeDetailsContainer>
      }
    />
  );
};
